'use client';

import { StoreApi, useStore } from 'zustand';

import React, {
	ElementType,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from 'react';

import { Modal } from '@/shared/components';

import createDialogContentStore, {
	BaseDialogStoreState,
	DialogObject,
} from '../stores/base-dialog-store';

interface ContextValue {
	store: StoreApi<BaseDialogStoreState<ElementType>>;
}

const DialogContext = createContext<ContextValue>({} as ContextValue);

type Selector<TState, TSelected> = (state: TState) => TSelected;

export function useDialogs<TSelected>(
	selector: Selector<BaseDialogStoreState, TSelected>
): TSelected {
	const value = useContext(DialogContext);

	return useStore(value.store || {}, selector);
}

export default function DialogProvider({ children }: React.PropsWithChildren) {
	const store = useMemo(() => {
		return createDialogContentStore();
	}, []);

	return (
		<DialogContext.Provider
			value={{
				store,
			}}
		>
			{children}
			<DefaultDialog />
		</DialogContext.Provider>
	);
}

function DefaultDialog() {
	const dialogs = useDialogs((state) => state.dialogs);

	useEffect(() => {
		if (dialogs?.length === 0) {
			document.body.style.pointerEvents = 'auto';
		}
	}, [dialogs?.length]);

	return (
		<RenderRecursiveDialogs
			dialogs={dialogs}
			index={0}
		/>
	);
}

function RenderRecursiveDialogs({
	dialogs = [],
	index = 0,
}: {
	dialogs: DialogObject<ElementType>[];
	index: number;
}) {
	const { Component, props, open, onDialogClose, title, description, contentClassName } =
		dialogs[index] || {};
	const setOpen = useDialogs((state) => state.setDialogOpen);
	const clearDialogContent = useDialogs((state) => state.clearDialogContent);

	const onOpenChange = useCallback(
		(open: boolean) => {
			setOpen(open, index);

			if (!open) {
				onDialogClose?.();
				clearDialogContent();
			}
		},
		[onDialogClose, clearDialogContent, setOpen, index]
	);

	if (index >= dialogs.length || !dialogs[index]) return null;
	return (
		<Modal
			open={open}
			onOpenChange={onOpenChange}
			titleContent={title}
			descriptionContent={description}
			contentClassName={contentClassName}
		>
			{Component ? <Component {...(props || {})} /> : null}

			<RenderRecursiveDialogs
				dialogs={dialogs}
				index={index + 1}
			/>
		</Modal>
	);
}
